/**
 * Frontend date utilities
 * All date operations use the browser's local timezone
 */

/**
 * Get the user's browser timezone
 * Returns IANA timezone identifier (e.g., "Asia/Kolkata", "America/New_York")
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error("[DateUtils] Error detecting browser timezone:", error);
    return "UTC";
  }
}

/**
 * Get the local date (YYYY-MM-DD) from a timestamp in the browser's timezone
 * This ensures dates are displayed correctly for the user's location
 *
 * Example: 2025-12-10T20:00:00Z in browser timezone Asia/Kolkata
 * UTC: 2025-12-10 20:00
 * IST: 2025-12-11 01:30 (next day!)
 * Returns: "2025-12-11"
 */
export function getLocalDate(timestamp: Date | string): string {
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;

    // Get year, month, day in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("[DateUtils] Error getting local date:", error);
    // Fallback to ISO string
    return new Date(timestamp).toISOString().split("T")[0];
  }
}

/**
 * Get today's date in local timezone (YYYY-MM-DD)
 */
export function getLocalToday(): string {
  return getLocalDate(new Date());
}

/**
 * Format a date in the browser's locale
 * Returns formatted string like "12/11/2025" or "11/12/2025" depending on locale
 */
export function formatLocalDate(
  timestamp: Date | string,
  options: Intl.DateTimeFormatOptions = {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  }
): string {
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    console.error("[DateUtils] Error formatting local date:", error);
    return new Date(timestamp).toISOString().split("T")[0];
  }
}

/**
 * Format a time in the browser's locale
 * Returns formatted string like "11:30 PM" or "23:30" depending on locale
 */
export function formatLocalTime(
  timestamp: Date | string,
  options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }
): string {
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleTimeString(undefined, options);
  } catch (error) {
    console.error("[DateUtils] Error formatting local time:", error);
    return new Date(timestamp).toISOString().split("T")[1].slice(0, 5);
  }
}

/**
 * Format a full date and time in the browser's locale
 * Returns formatted string like "Dec 11, 2025 at 11:30 PM"
 */
export function formatLocalDateTime(
  timestamp: Date | string,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }
): string {
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleString(undefined, options);
  } catch (error) {
    console.error("[DateUtils] Error formatting local date/time:", error);
    return new Date(timestamp).toISOString();
  }
}

/**
 * Check if two timestamps are on the same day in local timezone
 */
export function isSameLocalDay(date1: Date | string, date2: Date | string): boolean {
  return getLocalDate(date1) === getLocalDate(date2);
}

/**
 * Get the start of today in local timezone as UTC timestamp
 * Useful for filtering "today's" data
 */
export function getLocalStartOfToday(): Date {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Get the end of today in local timezone as UTC timestamp
 */
export function getLocalEndOfToday(): Date {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  return new Date(year, month, day, 23, 59, 59, 999);
}

/**
 * Parse a date string (YYYY-MM-DD) as local date
 * Returns Date object at midnight in local timezone
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * Get relative time description
 * Returns "Today", "Yesterday", or formatted date
 */
export function getRelativeDateLabel(timestamp: Date | string): string {
  const date = getLocalDate(timestamp);
  const today = getLocalToday();

  if (date === today) {
    return "Today";
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = getLocalDate(yesterday);

  if (date === yesterdayStr) {
    return "Yesterday";
  }

  // Return formatted date
  return formatLocalDate(timestamp, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
